"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EarlyAccessBuzz() {
  const testimonials = [{
    quote: "Ruchnii Studios has completely redefined how I approach digital storytelling. The tools are simply revolutionary.",
    author: "Elena V.",
    position: "Independent Filmmaker",
    dogName: "Luna",
    dogBreed: "Golden Retriever",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/elena_v_filmmaker.webp"
  }, {
    quote: "The precision and cinematic quality provided by Ruchnii Studios are unmatched in the current market.",
    author: "Marcus T.",
    position: "Creative Director",
    dogName: "Shadow",
    dogBreed: "Border Collie",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/marcus_t_creative_director.png"
  }, {
    quote: "An incredible platform. It feels like having a professional animation studio right at my fingertips.",
    author: "Sarah J.",
    position: "Digital Artist",
    dogName: "Cooper",
    dogBreed: "Beagle",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/sarah_j_digital_artist.png"
  }];
  const miniCards = [{
    quote: "A masterclass in accessible cinematic technology.",
    author: "JULIAN R.",
    dogInfo: "Max — Husky"
  }, {
    quote: "The rendering speed and quality are breathtaking.",
    author: "CLARA D.",
    dogInfo: "Daisy — Corgi"
  }, {
    quote: "Finally, a studio that understands the creator's vision.",
    author: "VICTOR H.",
    dogInfo: "Rocky — Boxer"
  }, {
    quote: "Ruchnii Studios is the future of independent film.",
    author: "BEATRICE L.",
    dogInfo: "Milo — Poodle"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: miniRef,
    isVisible: miniVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const changeTestimonial = (newIndex: number) => {
    if (newIndex === activeIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(newIndex);
      setIsTransitioning(false);
    }, 400);
  };
  const goPrev = () => changeTestimonial(activeIndex === 0 ? testimonials.length - 1 : activeIndex - 1);
  const goNext = () => changeTestimonial((activeIndex + 1) % testimonials.length);
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % testimonials.length);
        setIsTransitioning(false);
      }, 400);
    }, 7000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  const current = testimonials[activeIndex];
  return <section data-section-id="3966" id="early-access-buzz" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-[200px] px-6 md:px-12 lg:px-20">
      {}
      <div ref={headerRef} className={`absolute top-12 md:top-16 lg:top-20 left-6 md:left-12 lg:left-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}>
        <span className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-muted-foreground">
          04 — Community
        </span>
      </div>

      {}
      <div ref={quoteRef} className={`relative max-w-[1000px] mx-auto mt-16 md:mt-20 transition-all duration-1000 ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div aria-hidden="true" className="absolute -top-16 md:-top-24 -left-4 md:-left-12 font-sans font-bold leading-none select-none pointer-events-none" style={{
        fontSize: "clamp(120px, 18vw, 180px)",
        color: "var(--primary)",
        opacity: 0.4
      }}>
          &ldquo;
        </div>

        {}
        <div aria-hidden="true" className="absolute -bottom-28 md:-bottom-36 -right-4 md:-right-12 font-sans font-bold leading-none select-none pointer-events-none" style={{
        fontSize: "clamp(120px, 18vw, 180px)",
        color: "var(--primary)",
        opacity: 0.4
      }}>
          &rdquo;
        </div>

        {}
        <div className="relative z-10 px-4 md:px-12 overflow-hidden">
          <blockquote key={activeIndex} className={`font-serif italic text-center text-2xl md:text-4xl lg:text-[56px] leading-[1.25] transition-all ease-out ${isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`} style={{
          transitionDuration: "800ms"
        }}>
            {current.quote}
          </blockquote>

          {}
          <div className={`mt-12 md:mt-16 flex flex-col items-center gap-4 transition-all ease-out ${isTransitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`} style={{
          transitionDuration: "800ms",
          transitionDelay: "100ms"
        }}>
            <img src={current.avatar} data-wvc-srcset={current.avatar + "-wvc-srcset"} data-wvc-sizes={current.avatar + "-wvc-sizes"} alt={current.author} loading="lazy" decoding="async" className="w-12 h-12 rounded-lg object-cover grayscale border border-border" />
            <div className="flex flex-col items-center gap-1">
              <span className="font-default font-semibold text-sm md:text-base uppercase tracking-[0.15em] text-foreground">
                {current.author}
              </span>
              <span className="font-serif italic text-sm text-muted-foreground">
                {current.dogName} — {current.dogBreed}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                {current.position}
              </span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative max-w-[1000px] mx-auto mt-16 md:mt-20 flex items-center justify-center gap-8">
        <Button variant="ghost" size="icon" onClick={goPrev} aria-label="Previous testimonial" className="h-10 w-10 text-foreground hover:text-primary hover:bg-transparent transition-colors">
          <ChevronLeft className="h-6 w-6" strokeWidth={1} />
        </Button>

        <div className="flex items-center gap-3">
          {testimonials.map((_, i) => <Button key={i} data-index={i} variant="ghost" onClick={() => changeTestimonial(i)} aria-label={`Go to testimonial ${i + 1}`} className="py-3 px-0 h-auto hover:bg-transparent">
              <span className={`block h-[2px] transition-all duration-500 ${i === activeIndex ? "w-10 bg-primary" : "w-6 bg-muted-foreground/40 hover:bg-muted-foreground"}`} />
            </Button>)}
        </div>

        <Button variant="ghost" size="icon" onClick={goNext} aria-label="Next testimonial" className="h-10 w-10 text-foreground hover:text-primary hover:bg-transparent transition-colors">
          <ChevronRight className="h-6 w-6" strokeWidth={1} />
        </Button>
      </div>

      {}
      <div ref={miniRef} className="mt-24 md:mt-32 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {miniCards.map((card, i) => <div data-index={i} key={i} className={`relative w-full h-[160px] p-6 flex flex-col justify-between backdrop-blur-md border border-border/60 shadow-lg transition-all duration-700 hover:border-primary/40 hover:-translate-y-1 ${miniVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        backgroundColor: "hsl(0 0% 100% / 0.04)",
        transitionDelay: `${i * 120}ms`
      }}>
            <p className="font-serif italic text-sm leading-snug text-foreground">
              &ldquo;{card.quote}&rdquo;
            </p>
            <div className="flex flex-col gap-0.5">
              <span className="font-default text-[10px] uppercase tracking-[0.2em] text-foreground">
                {card.author}
              </span>
              <span className="font-serif italic text-xs text-muted-foreground">
                {card.dogInfo}
              </span>
            </div>
          </div>)}
      </div>
    </section>;
}